import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { CheckCircle2, Search } from "lucide-react";
import {
  useCreateRoleMutation,
  useGetAssignableEmployeesQuery,
  useGetAssignedRolePrivilegesQuery,
  useGetPrivilegeCatalogQuery,
} from "../store";
import { rolePrimaryLabel } from "../utils/roleDisplay";
import { ApiErrorText, apiCaughtError } from "../components/ApiErrorText";

const EMPTY_FORM = {
  roleName: "",
  roleDescription: "",
};

const FIELD_ERROR_FOCUS_ORDER = ["roleName", "roleDescription", "privilegeIds", "empIds"];

const FIELD_ERROR_FOCUS_IDS = {
  roleName: "create-role-name",
  roleDescription: "create-role-description",
  privilegeIds: "create-role-privileges-search",
  empIds: "create-role-users-search",
};

function getFirstFieldErrorKey(errors) {
  return FIELD_ERROR_FOCUS_ORDER.find((key) => String(errors?.[key] ?? "").trim());
}

function normalizeRoleName(value) {
  return String(value ?? "").trim().toUpperCase();
}

function normalizeRoleDescription(value) {
  return String(value ?? "").trim();
}

function collectDuplicateRoleErrors(roleName, roleDescription, existingRoles) {
  const errors = {};
  const normalizedName = normalizeRoleName(roleName);
  const normalizedDescription = normalizeRoleDescription(roleDescription);

  for (const role of existingRoles) {
    const existingName = normalizeRoleName(role?.roleName);
    const existingDescription = normalizeRoleDescription(role?.roleDescription);

    if (existingName && existingName === normalizedName) {
      errors.roleName = "A role with this name already exists.";
    }
    if (
      normalizedDescription &&
      existingDescription &&
      existingDescription.localeCompare(normalizedDescription, undefined, {
        sensitivity: "accent",
      }) === 0
    ) {
      errors.roleDescription = "A role with this description already exists.";
    }
  }

  return errors;
}

function formatFieldErrors(err) {
  const errors = err?.data?.errors;
  if (!errors || typeof errors !== "object") {
    return {};
  }
  return Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key, String(value ?? "").trim()]),
  );
}

function formatSubmitError(err) {
  const fieldErrors = formatFieldErrors(err);
  const firstFieldError = Object.values(fieldErrors).find(Boolean);
  if (firstFieldError) {
    return firstFieldError;
  }
  if (err?.status === 403) {
    return "You do not have permission to create roles.";
  }
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to create role (HTTP ${err.status}).`;
  }
  return "Unable to create role.";
}

function formatEmployeeLabel(employee) {
  const name = String(employee?.displayName ?? "").trim();
  if (name) {
    return name;
  }
  if (employee?.empId != null) {
    return `Employee #${employee.empId}`;
  }
  return "—";
}

function SelectionList({
  title,
  description,
  searchPlaceholder,
  searchInputId,
  searchTerm,
  onSearchChange,
  isLoading,
  isError,
  emptyMessage,
  items,
  selectedIds,
  onToggle,
  getKey,
  renderLabel,
  renderMeta,
}) {
  const q = searchTerm.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!q) {
      return items;
    }
    return items.filter((item) => renderLabel(item).toLowerCase().includes(q));
  }, [items, q, renderLabel]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h2>
        {description ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
        ) : null}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            id={searchInputId}
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            disabled={isLoading || isError}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-800">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-6 text-sm text-red-600 dark:text-red-400 text-center">
            Unable to load options.
          </p>
        ) : filteredItems.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">{emptyMessage}</p>
        ) : (
          filteredItems.map((item) => {
            const key = getKey(item);
            const checked = selectedIds.has(key);
            return (
              <label
                key={key}
                className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(key)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                    {renderLabel(item)}
                  </span>
                  {renderMeta ? (
                    <span className="block mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {renderMeta(item)}
                    </span>
                  ) : null}
                </span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function CreateNewRole() {
  const {
    data: privilegeCatalog = [],
    isLoading: privilegesLoading,
    isError: privilegesError,
  } = useGetPrivilegeCatalogQuery();
  const {
    data: assignableEmployees = [],
    isLoading: employeesLoading,
    isError: employeesError,
  } = useGetAssignableEmployeesQuery();
  const { data: existingRoles = [] } = useGetAssignedRolePrivilegesQuery();
  const [createRole, { isLoading: isSaving }] = useCreateRoleMutation();

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedPrivilegeIds, setSelectedPrivilegeIds] = useState(() => new Set());
  const [selectedEmpIds, setSelectedEmpIds] = useState(() => new Set());
  const [privilegeSearch, setPrivilegeSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);

  const privileges = useMemo(
    () => (Array.isArray(privilegeCatalog) ? privilegeCatalog : []),
    [privilegeCatalog],
  );
  const employees = useMemo(
    () => (Array.isArray(assignableEmployees) ? assignableEmployees : []),
    [assignableEmployees],
  );

  useEffect(() => {
    const firstErrorKey = getFirstFieldErrorKey(fieldErrors);
    if (!firstErrorKey) {
      return;
    }
    const focusId = FIELD_ERROR_FOCUS_IDS[firstErrorKey];
    if (!focusId) {
      return;
    }
    const el = document.getElementById(focusId);
    if (!el) {
      return;
    }
    requestAnimationFrame(() => {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [fieldErrors]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    setSubmitError("");
    setSuccess(null);
  };

  const togglePrivilege = (privilegeId) => {
    setSelectedPrivilegeIds((prev) => {
      const next = new Set(prev);
      if (next.has(privilegeId)) {
        next.delete(privilegeId);
      } else {
        next.add(privilegeId);
      }
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, privilegeIds: "" }));
    setSubmitError("");
    setSuccess(null);
  };

  const toggleEmployee = (empId) => {
    setSelectedEmpIds((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) {
        next.delete(empId);
      } else {
        next.add(empId);
      }
      return next;
    });
    setFieldErrors((prev) => ({ ...prev, empIds: "" }));
    setSubmitError("");
    setSuccess(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setFieldErrors({});
    setSuccess(null);

    const roleName = normalizeRoleName(form.roleName);
    const roleDescription = normalizeRoleDescription(form.roleDescription);
    const localErrors = {};

    if (!roleName) {
      localErrors.roleName = "Role name is required.";
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(roleName)) {
      localErrors.roleName =
        "Use uppercase letters, digits, and underscores (e.g. ACCT_MANAGER).";
    }

    if (!roleDescription) {
      localErrors.roleDescription = "Role description is required.";
    }

    Object.assign(
      localErrors,
      collectDuplicateRoleErrors(roleName, roleDescription, existingRoles),
    );

    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    try {
      const result = await createRole({
        roleName,
        roleDescription,
        privilegeIds: Array.from(selectedPrivilegeIds),
        empIds: Array.from(selectedEmpIds),
      }).unwrap();
      setSuccess(result);
      setForm(EMPTY_FORM);
      setSelectedPrivilegeIds(new Set());
      setSelectedEmpIds(new Set());
      setPrivilegeSearch("");
      setEmployeeSearch("");
    } catch (err) {
      const apiFieldErrors = formatFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      }
      setSubmitError(apiCaughtError(err, formatSubmitError));
    }
  };

  const optionsLoading = privilegesLoading || employeesLoading;
  const optionsError = privilegesError || employeesError;

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Create New Role
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Add a billing role and optionally assign privileges and users
        </p>
      </div>

      {success ? (
        <div className="mb-6 rounded border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 px-4 py-3 text-sm text-green-900 dark:text-green-100">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Role created</p>
              <p className="mt-1">
                <span className="font-medium">
                  {rolePrimaryLabel(success.roleDescription, success.roleName) || success.roleName}
                </span>{" "}
                was created with {(success.privileges ?? []).length} privilege
                {(success.privileges ?? []).length === 1 ? "" : "s"} and{" "}
                {(success.employees ?? []).length} user
                {(success.employees ?? []).length === 1 ? "" : "s"} assigned.
              </p>
              <Link
                to="/role-privileges"
                className="mt-2 inline-block text-green-800 dark:text-green-200 underline hover:no-underline"
              >
                View assigned role privileges
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      <div className="max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-5">
            <div>
              <label
                htmlFor="create-role-name"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
              >
                Role name
              </label>
              <input
                id="create-role-name"
                type="text"
                value={form.roleName}
                onChange={(e) => updateField("roleName", e.target.value.toUpperCase())}
                disabled={isSaving}
                placeholder="ACCT_MANAGER"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Unique role code stored in act_billing_role.role_name
              </p>
              {fieldErrors.roleName ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.roleName}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor="create-role-description"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
              >
                Description
              </label>
              <textarea
                id="create-role-description"
                value={form.roleDescription}
                onChange={(e) => updateField("roleDescription", e.target.value)}
                disabled={isSaving}
                rows={3}
                placeholder="Describe this role"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Unique description stored in act_billing_role.role_description
              </p>
              {fieldErrors.roleDescription ? (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {fieldErrors.roleDescription}
                </p>
              ) : null}
            </div>
          </div>

          <SelectionList
            title="Privileges"
            description="Optional. Assign privileges to this role via act_billing_role_privilege."
            searchPlaceholder="Search privileges…"
            searchInputId="create-role-privileges-search"
            searchTerm={privilegeSearch}
            onSearchChange={setPrivilegeSearch}
            isLoading={privilegesLoading}
            isError={privilegesError}
            emptyMessage={privilegeSearch.trim() ? "No privileges match your search." : "No privileges found."}
            items={privileges}
            selectedIds={selectedPrivilegeIds}
            onToggle={togglePrivilege}
            getKey={(item) => item.privilegeId}
            renderLabel={(item) => item.privilegeName || "—"}
            renderMeta={(item) =>
              [item.privilegeDescription, item.componentPage].filter(Boolean).join(" · ") || null
            }
          />
          {fieldErrors.privilegeIds ? (
            <p className="text-sm text-red-600 dark:text-red-400">{fieldErrors.privilegeIds}</p>
          ) : null}

          <SelectionList
            title="Users"
            description="Optional. Assign active employees to this role via act_employee.role_id."
            searchPlaceholder="Search users…"
            searchInputId="create-role-users-search"
            searchTerm={employeeSearch}
            onSearchChange={setEmployeeSearch}
            isLoading={employeesLoading}
            isError={employeesError}
            emptyMessage={employeeSearch.trim() ? "No users match your search." : "No assignable users found."}
            items={employees}
            selectedIds={selectedEmpIds}
            onToggle={toggleEmployee}
            getKey={(item) => item.empId}
            renderLabel={formatEmployeeLabel}
            renderMeta={(item) =>
              [item.empId != null ? `Emp ID: ${item.empId}` : null, item.emailAddress]
                .filter(Boolean)
                .join(" · ")
            }
          />
          {fieldErrors.empIds ? (
            <p className="text-sm text-red-600 dark:text-red-400">{fieldErrors.empIds}</p>
          ) : null}

          {optionsError ? (
            <div className="rounded border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
              Some assignment options could not be loaded. You can still create the role, but privilege
              and user lists may be incomplete.
            </div>
          ) : null}

          {submitError ? (
            <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              <ApiErrorText error={submitError} />
            </div>
          ) : null}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving || optionsLoading}
              className="app-btn-gloss disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Creating…" : "Create role"}
            </button>
            <Link
              to="/role-privileges"
              className="app-btn-gloss-cancel"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export { CreateNewRole };
